import { useMemo, useState } from 'react';
import type { DataTableMode, DataTablePagination, DataTableSort } from '@odo/shared-ui';
import {
    useIteroPatients,
    useIteroPatientsInfinite,
    useIteroPatientsScrollReset,
    type IteroBrowseScope
} from '../../../../hooks/useIteroBrowse';
import type { IteroPatient, IteroPatientsFilterParams } from '../../../../services/iteroBrowse';
import { browseErrorText } from './browseErrorText';
import { FIRST_PAGE, pageScope, scrollRowsMeta } from './browsePaging';
import { useIteroBrowserScope } from './iteroBrowserScope';
import { isPatientSearchDisabled } from './iteroBrowseErrors';
import {
    DEFAULT_PATIENT_FILTERS,
    PATIENTS_PAGE_SIZE,
    buildPatientFilterParams,
    hasPatientSearchFilters,
    isDefaultPatientFilters,
    mergePatientPages,
    patientPageCount,
    patientPageParams,
    type PatientFilterValues
} from './patientQuery';
import {
    DEFAULT_PATIENT_SORT,
    sortAfterHeaderClick,
    type PatientSort,
    type PatientSortKey
} from './patientSort';
import { useBrowseFilters, useScopedPage } from './useBrowseFilters';

const NO_PATIENTS: readonly IteroPatient[] = [];
const noop = (): void => undefined;

export interface PatientsBrowse {
    filters: PatientFilterValues;
    updateFilters: (patch: Partial<PatientFilterValues>) => void;
    clearFilters: () => void;
    showClear: boolean;
    hasFilters: boolean;
    sort: DataTableSort<PatientSortKey>;
    mode: DataTableMode;
    patients: readonly IteroPatient[];
    total: number | null;
    pagination: DataTablePagination;
    isLoading: boolean;
    isUpdating: boolean;
    isDisabled: boolean;
    errorMessage: string | null;
    hasMore: boolean;
    isLoadingMore: boolean;
    loadMore: () => void;
    loadMoreError: string | null;
    retry: () => void;
    resetScroll: () => void;
    // Changes whenever the list starts over (filters, search, sort, mode), to scroll back to the top.
    listKey: string;
}

// One shape for both modes, so the browse state never branches on how rows were fetched.
interface PatientRows {
    patients: readonly IteroPatient[];
    total: number | undefined;
    nextOffset: number | null;
    isPending: boolean;
    isShowingPrevious: boolean;
    error: unknown;
    loadMoreError: unknown;
    hasMore: boolean;
    isLoadingMore: boolean;
    loadMore: () => void;
    retry: () => void;
}

function usePagedPatients(
    scope: IteroBrowseScope,
    filterParams: IteroPatientsFilterParams,
    page: number,
    enabled: boolean
): PatientRows {
    const params = useMemo(() => patientPageParams(filterParams, page), [filterParams, page]);
    const query = useIteroPatients(scope, params, enabled);
    return {
        patients: query.data?.patients ?? NO_PATIENTS,
        total: query.data?.total,
        nextOffset: query.data?.nextOffset ?? null,
        isPending: query.isPending,
        isShowingPrevious: query.isFetching && query.isPlaceholderData,
        error: query.isError ? query.error : null,
        loadMoreError: null,
        hasMore: false,
        isLoadingMore: false,
        loadMore: noop,
        retry: () => void query.refetch()
    };
}

function useScrolledPatients(
    scope: IteroBrowseScope,
    filterParams: IteroPatientsFilterParams,
    enabled: boolean
): PatientRows {
    const query = useIteroPatientsInfinite(scope, filterParams, enabled);
    const patients = useMemo(() => mergePatientPages(query.data?.pages ?? []), [query.data]);
    return {
        patients,
        total: query.data?.pages[0]?.total,
        nextOffset: null,
        ...scrollRowsMeta(query)
    };
}

function patientsPagination(
    mode: DataTableMode,
    page: number,
    rows: PatientRows,
    onPage: (page: number) => void
): DataTablePagination {
    const { total } = rows;
    if (mode === 'infinite') return { page: FIRST_PAGE, totalPages: FIRST_PAGE, total, onPage };
    return {
        page,
        total,
        totalPages: patientPageCount(page, PATIENTS_PAGE_SIZE, total ?? 0, rows.nextOffset),
        onPage
    };
}

export function usePatientsBrowse(today: string, mode: DataTableMode): PatientsBrowse {
    const scope = useIteroBrowserScope();
    const { filters, effective, isTyping, updateFilters, clearFilters } =
        useBrowseFilters<PatientFilterValues>(DEFAULT_PATIENT_FILTERS);
    const [sort, setSort] = useState<PatientSort>(DEFAULT_PATIENT_SORT);
    const filterParams = useMemo(
        () => buildPatientFilterParams(effective, sort, today),
        [effective, sort, today]
    );
    const scopeKey = pageScope(mode, JSON.stringify(filterParams));
    const [page, setPage] = useScopedPage(scopeKey);

    // Both queries are declared (hook order is fixed); only the active mode's one fetches.
    const paged = usePagedPatients(scope, filterParams, page, mode === 'paged');
    const scrolled = useScrolledPatients(scope, filterParams, mode === 'infinite');
    const rows = mode === 'paged' ? paged : scrolled;
    const resetScroll = useIteroPatientsScrollReset(scope);

    return {
        filters,
        updateFilters,
        clearFilters,
        showClear: !isDefaultPatientFilters(filters),
        hasFilters: hasPatientSearchFilters(effective, today),
        sort: {
            ...sort,
            onSort: (column) => setSort((current) => sortAfterHeaderClick(current, column))
        },
        mode,
        patients: rows.patients,
        total: rows.error || rows.total === undefined ? null : rows.total,
        pagination: patientsPagination(mode, page, rows, setPage),
        isLoading: rows.isPending,
        isUpdating: rows.isShowingPrevious || isTyping,
        isDisabled: isPatientSearchDisabled(rows.error),
        errorMessage: rows.error
            ? browseErrorText(rows.error, "Couldn't load iTero patients")
            : null,
        hasMore: rows.hasMore,
        isLoadingMore: rows.isLoadingMore,
        loadMore: rows.loadMore,
        loadMoreError: rows.loadMoreError
            ? browseErrorText(rows.loadMoreError, "Couldn't load more patients")
            : null,
        retry: rows.retry,
        resetScroll: () => resetScroll(filterParams),
        listKey: scopeKey
    };
}
